import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Chip,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useFileUploadBox } from '../hooks/useFileUploadBox';

const truncateFileName = (fileName, maxLength = 30) => {
  if (fileName.length <= maxLength) return fileName;
  const ext = fileName.split('.').pop();
  const base = fileName.substring(0, fileName.lastIndexOf('.'));
  return `${base.substring(0, maxLength - ext.length - 3)}...${ext}`;
};

/**
 * @param {Object}   props
 * @param {string}   props.label
 * @param {boolean}  props.required
 * @param {File[]}   props.files          - Array de File objects
 * @param {Function} props.onFilesChange  - (File[]) => void
 * @param {number}   props.maxFiles
 * @param {number}   props.maxSizeMB
 * @param {boolean}  props.disabled
 * @param {boolean}  props.compressFiles
 * @param {Function} props.setMessage
 */
const FileUploadBox = ({
  label,
  required = false,
  files = [],
  onFilesChange,
  maxFiles = 3,
  maxSizeMB = 4,
  disabled = false,
  compressFiles = true,
  setMessage,
}) => {
  const {
    fileInputRef,
    isDragging,
    isCompressing,
    compressionProgress,
    isMaxFiles,
    getFileIconType,
    formatFileSize,
    getTotalSizeMB,
    handleButtonClick,
    handleInputChange,
    handleRemoveFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  } = useFileUploadBox({ files, onFilesChange, maxFiles, disabled, compressFiles, setMessage });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const totalSizeMB = getTotalSizeMB();
  const showRequiredError = required && files.length === 0;

  const getFileIcon = (file) => {
    const type = getFileIconType(file);
    const sx = { fontSize: 30, color: '#616161ba' };
    if (type === 'pdf') return <PdfIcon sx={sx} />;
    if (type === 'image') return <ImageIcon sx={sx} />;
    return <FileIcon sx={sx} />;
  };

  return (
    <Card
      elevation={0}
      variant='outlined'
      sx={{
        border: '1px solid',
        borderColor: showRequiredError ? 'error.main' : 'divider',
        borderRadius: 2,
        boxShadow: 'none',
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle1' fontWeight='600' color={'text.primary'}>
            {label}
            {required && (
              <Typography component='span' color='error.main'>
                {' '}*
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Área drag & drop — solo desktop */}
        {!isMobile && (
          <Box
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleButtonClick}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : showRequiredError ? 'error.light' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: isDragging ? alpha('#1976d2', 0.05) : 'background.paper',
              cursor: disabled || isMaxFiles || isCompressing ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              mb: files.length > 0 ? 2 : 0,
              '&:hover': !disabled && !isMaxFiles && !isCompressing && {
                borderColor: 'primary.main',
                backgroundColor: alpha('#1976d2', 0.02),
              },
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: isDragging ? 'primary.main' : disabled || isMaxFiles || isCompressing ? 'text.disabled' : 'action.active',
                mb: 1,
              }}
            />
            <Typography variant='body1' gutterBottom fontWeight='500'>
              {isCompressing
                ? 'Comprimiendo archivos...'
                : isDragging
                  ? 'Suelta los archivos aquí'
                  : isMaxFiles
                    ? `Máximo de ${maxFiles} archivos alcanzado`
                    : 'Arrastrá archivos aquí o hacé clic para seleccionar'}
            </Typography>
            <Button
              variant='contained'
              startIcon={<CloudUploadIcon />}
              disabled={disabled || isMaxFiles || isCompressing}
              sx={{ mt: 1 }}
              onClick={(e) => { e.stopPropagation(); handleButtonClick(); }}
            >
              {isCompressing ? 'Comprimiendo...' : 'Seleccionar archivos'}
            </Button>
          </Box>
        )}

        {/* Input oculto */}
        <input
          ref={fileInputRef}
          type='file'
          accept='.pdf,.jpg,.jpeg,image/jpeg,image/jpg,application/pdf'
          multiple
          onChange={handleInputChange}
          style={{ display: 'none' }}
          disabled={disabled || isMaxFiles}
        />

        {/* Progreso de compresión */}
        {isCompressing && compressionProgress !== null && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant='caption' color='primary'>Comprimiendo archivos...</Typography>
              <Typography variant='caption' fontWeight='600' color='primary'>
                {Math.round(compressionProgress)}%
              </Typography>
            </Box>
            <LinearProgress variant='determinate' value={compressionProgress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        )}

        {/* Lista de archivos */}
        {files.length > 0 && (
          <Box>
            <Stack spacing={1}>
              {files.map((file, index) => (
                <Card
                  key={`${file.name}-${file.size}-${index}`}
                  elevation={0}
                  variant='outlined'
                  sx={{ p: 1.5, boxShadow: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 2 }}>{getFileIcon(file)}</Box>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant='body2'
                        fontWeight='500'
                        noWrap
                        title={file.name}
                        sx={{ mb: 0.5 }}
                      >
                        {truncateFileName(file.name, isMobile ? 25 : 50)}
                      </Typography>
                      <Chip label={formatFileSize(file.size)} size='small' variant='filled' />
                    </Box>

                    <IconButton
                      onClick={() => handleRemoveFile(index)}
                      disabled={disabled}
                      color='primary'
                      size='small'
                      sx={{ ml: 1 }}
                      aria-label={`Eliminar ${file.name}`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Stack>

            {/* Indicador de tamaño total */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant='caption' fontWeight='600'>
                  Archivos adjuntados ({files.length}/{maxFiles})
                </Typography>
                <Typography
                  variant='caption'
                  fontWeight='600'
                  color={totalSizeMB > maxSizeMB ? 'error.main' : 'text.secondary'}
                >
                  {totalSizeMB.toFixed(2)} MB / {maxSizeMB} MB
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={Math.min((totalSizeMB / maxSizeMB) * 100, 100)}
                color={totalSizeMB > maxSizeMB ? 'error' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          </Box>
        )}

        {/* Botón mobile */}
        {isMobile && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant='outlined'
              startIcon={<CloudUploadIcon />}
              fullWidth
              disabled={disabled || isMaxFiles || isCompressing}
              onClick={handleButtonClick}
              sx={{ py: 1.5, borderStyle: 'dashed', borderWidth: 2 }}
            >
              {isCompressing ? 'Comprimiendo...' : isMaxFiles ? 'Límite alcanzado' : 'Seleccionar archivos'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FileUploadBox;
